import { useEffect, useRef, useState } from "react";
import { z } from "zod";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { HoneypotFields } from "@/components/site/HoneypotFields";
import { evaluateBotSignals, emailField, nameField } from "@/lib/form-security";
import { supabase } from "@/integrations/supabase/client";
import { trackCTA, trackConversion } from "@/lib/tracking";
import { useHeroFormStart } from "@/lib/hero-analytics";
import {
  BookingSlotPicker,
  TIME_SLOTS,
  formatSlot,
  nextBusinessDays,
  toISODate,
  type BookingSlot,
} from "@/components/advocacy/BookingSlotPicker";

const URGENCY = [
  "Immediate — meeting or deadline within 2 weeks",
  "Soon — within 30 days",
  "Planning ahead — next 60–90 days",
  "Exploring options",
] as const;

const schema = z.object({
  fullName: nameField,
  email: emailField,
  phone: z.string().trim().max(30).optional(),
  organization: z.string().trim().max(150).optional(),
  urgency: z
    .string()
    .refine((v) => (URGENCY as readonly string[]).includes(v), {
      message: "Select a timeline.",
    }),
  situation: z
    .string()
    .trim()
    .min(20, { message: "Give us at least a sentence or two about the situation." })
    .max(2000),
  scheduledDate: z.string().min(1, { message: "Pick a preferred day." }),
  scheduledTime: z
    .string()
    .refine((v) => (TIME_SLOTS as readonly string[]).includes(v), {
      message: "Pick a preferred time.",
    }),
});

type Values = z.infer<typeof schema>;
type FieldErrors = Partial<Record<keyof Values, string>>;

export type StrategySessionModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Display label of the retainer tier the visitor is applying from. */
  tierLabel: string;
  /** Stable identifier for analytics (e.g. "priority"). */
  tierId?: string;
  /** Where on the site the modal was opened from. */
  source?: string;
};

const inputBase =
  "w-full bg-navy/70 border px-4 py-3 text-white text-sm placeholder:text-silver/50 focus:outline-none focus:border-gold transition-colors";

export function StrategySessionModal({
  open,
  onOpenChange,
  tierLabel,
  tierId,
  source = "advocacy_tier_card",
}: StrategySessionModalProps) {
  const defaultSlot: BookingSlot = {
    date: toISODate(nextBusinessDays(1)[0]),
    time: TIME_SLOTS[0],
  };
  const [values, setValues] = useState<Values>({
    fullName: "",
    email: "",
    phone: "",
    organization: "",
    urgency: "",
    situation: "",
    scheduledDate: defaultSlot.date,
    scheduledTime: defaultSlot.time,
  });
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const heroFormStart = useHeroFormStart("strategy_session_modal");
  const startedAt = useRef(Date.now());
  const interacted = useRef(false);

  useEffect(() => {
    if (open) {
      startedAt.current = Date.now();
      interacted.current = false;
      setDone(false);
      setErrors({});
      trackCTA("strategy_session_modal_open", { tier: tierId ?? tierLabel, source });
    }
  }, [open, tierId, tierLabel, source]);

  const setField = <K extends keyof Values>(k: K, v: string) => {
    setValues((prev) => ({ ...prev, [k]: v }));
    if (errors[k]) setErrors((prev) => ({ ...prev, [k]: undefined }));
  };

  const err = (k: keyof Values) => (errors[k] ? "border-red-500/70" : "border-border");

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitting) return;

    const signals = evaluateBotSignals({
      formData: new FormData(e.currentTarget),
      startedAt: startedAt.current,
      interacted: interacted.current,
    });
    if (signals.verdict === "drop") {
      trackCTA("strategy_session_modal_blocked", { reason: signals.reason });
      setDone(true);
      return;
    }
    if (signals.verdict === "challenge") {
      toast.error(signals.message);
      return;
    }

    const parsed = schema.safeParse(values);
    if (!parsed.success) {
      const next: FieldErrors = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0] as keyof FieldErrors;
        if (!next[key]) next[key] = issue.message;
      }
      setErrors(next);
      return;
    }

    setSubmitting(true);
    const slotLabel = formatSlot(parsed.data.scheduledDate, parsed.data.scheduledTime);
    const timezone =
      Intl.DateTimeFormat().resolvedOptions().timeZone?.slice(0, 64) || null;
    const message = [
      `Strategy session application — ${tierLabel}`,
      ``,
      `Requested consultation: ${slotLabel}${timezone ? ` (visitor TZ: ${timezone})` : ""}`,
      `Timeline: ${parsed.data.urgency}`,
      parsed.data.phone ? `Phone: ${parsed.data.phone}` : null,
      parsed.data.organization ? `School / Organization: ${parsed.data.organization}` : null,
      ``,
      `Situation:`,
      parsed.data.situation,
    ]
      .filter(Boolean)
      .join("\n");

    // Booking record — the scheduled consultation itself.
    const { data: booking, error: bookingError } = await supabase
      .from("strategy_applications")
      .insert({
        full_name: parsed.data.fullName,
        email: parsed.data.email.toLowerCase(),
        organization: parsed.data.organization?.trim() || null,
        role: null,
        focus: `FSAP strategy session — ${tierLabel}`,
        current_stage: parsed.data.urgency,
        desired_outcome: parsed.data.situation,
        timezone,
        scheduled_date: parsed.data.scheduledDate,
        scheduled_time: parsed.data.scheduledTime,
        status: "pending",
      })
      .select("id")
      .maybeSingle();

    if (bookingError) {
      setSubmitting(false);
      console.error("Strategy booking insert error:", bookingError);
      toast.error("Something went wrong booking that time. Please try again.");
      return;
    }

    // Inquiry record — keeps the advocacy pipeline + analytics consistent.
    const { error } = await supabase.from("inquiry_submissions").insert({
      lane: "advocacy",
      full_name: parsed.data.fullName,
      email: parsed.data.email.toLowerCase(),
      phone: parsed.data.phone?.trim() || null,
      organization: parsed.data.organization?.trim() || null,
      event_or_focus: `Strategy session — ${tierLabel}`,
      date_or_timeline: `${slotLabel} · ${parsed.data.urgency}`,
      budget_range: tierLabel,
      message,
      source: `strategy-session-modal:${source}`,
    });

    // Confirmation email — best effort, never blocks the booking.
    try {
      await supabase.functions.invoke("send-transactional-email", {
        body: {
          templateName: "strategy-session-confirmation",
          recipientEmail: parsed.data.email.toLowerCase(),
          idempotencyKey: `strategy-session-${booking?.id ?? parsed.data.email}`,
          templateData: {
            name: parsed.data.fullName,
            tierLabel,
            slotLabel,
            timezone,
          },
        },
      });
    } catch (emailError) {
      console.warn("Strategy session confirmation email not sent:", emailError);
    }

    setSubmitting(false);

    if (error) {
      console.error("Strategy session insert error:", error);
    }

    trackConversion("strategy_session_submit", {
      tier: tierId ?? tierLabel,
      urgency: parsed.data.urgency,
      scheduled_date: parsed.data.scheduledDate,
      scheduled_time: parsed.data.scheduledTime,
      source,
    });
    trackConversion("inquiry_submit", { lane: "advocacy", source: "strategy_session_modal" });
    setDone(true);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl bg-navy border-gold/30 max-h-[90vh] overflow-y-auto"
        onPointerDownCapture={() => (interacted.current = true)}
        onKeyDownCapture={() => (interacted.current = true)}
      >
        {done ? (
          <div className="py-6 text-center">
            <CheckCircle2 className="h-10 w-10 text-gold mx-auto" />
            <DialogTitle className="font-display uppercase text-white text-xl mt-4">
              Session requested
            </DialogTitle>
            <p className="text-gold font-display text-sm mt-3">
              {formatSlot(values.scheduledDate, values.scheduledTime)}
            </p>
            <DialogDescription className="text-silver text-sm mt-3 leading-relaxed">
              Thank you. Your {tierLabel} strategy session request is booked for review.
              A confirmation is on its way to {values.email || "your inbox"}, and Dr.
              Sully&rsquo;s team will confirm the time within two business days.
            </DialogDescription>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="mt-7 inline-flex items-center justify-center gap-2 border border-gold/50 text-white font-display uppercase tracking-[0.2em] text-xs px-6 py-4 hover:bg-gold/10"
            >
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={onSubmit} onFocusCapture={heroFormStart} noValidate className="relative">
            <HoneypotFields />
            <DialogHeader>
              <span className="eyebrow text-gold text-left">{tierLabel}</span>
              <DialogTitle className="font-display font-bold uppercase text-white text-xl md:text-2xl text-left">
                Apply for a strategy session
              </DialogTitle>
              <DialogDescription className="text-silver text-sm text-left leading-relaxed">
                A focused working session to map your situation, options, and next steps —
                and to confirm whether this tier is the right fit before any retainer begins.
              </DialogDescription>
            </DialogHeader>

            <div className="grid md:grid-cols-2 gap-5 mt-6">
              <div>
                <label htmlFor="ssm-name" className="block text-silver text-[11px] uppercase tracking-[0.2em] mb-2">
                  Full name
                </label>
                <input
                  id="ssm-name"
                  name="full_name"
                  autoComplete="name"
                  maxLength={100}
                  value={values.fullName}
                  onChange={(e) => setField("fullName", e.target.value)}
                  aria-invalid={!!errors.fullName}
                  className={`${inputBase} ${err("fullName")}`}
                  placeholder="Jane Rivera"
                />
                {errors.fullName && (
                  <p className="text-red-400 text-xs mt-1.5">{errors.fullName}</p>
                )}
              </div>

              <div>
                <label htmlFor="ssm-email" className="block text-silver text-[11px] uppercase tracking-[0.2em] mb-2">
                  Email
                </label>
                <input
                  id="ssm-email"
                  name="email_address"
                  type="email"
                  autoComplete="email"
                  maxLength={255}
                  value={values.email}
                  onChange={(e) => setField("email", e.target.value)}
                  aria-invalid={!!errors.email}
                  className={`${inputBase} ${err("email")}`}
                  placeholder="you@example.com"
                />
                {errors.email && <p className="text-red-400 text-xs mt-1.5">{errors.email}</p>}
              </div>

              <div>
                <label htmlFor="ssm-phone" className="block text-silver text-[11px] uppercase tracking-[0.2em] mb-2">
                  Phone <span className="normal-case tracking-normal text-silver/60">(optional)</span>
                </label>
                <input
                  id="ssm-phone"
                  name="phone_number"
                  type="tel"
                  autoComplete="tel"
                  maxLength={30}
                  value={values.phone}
                  onChange={(e) => setField("phone", e.target.value)}
                  className={`${inputBase} border-border`}
                  placeholder="(210) 555-0142"
                />
              </div>

              <div>
                <label htmlFor="ssm-org" className="block text-silver text-[11px] uppercase tracking-[0.2em] mb-2">
                  School / Org <span className="normal-case tracking-normal text-silver/60">(optional)</span>
                </label>
                <input
                  id="ssm-org"
                  name="org_name"
                  maxLength={150}
                  value={values.organization}
                  onChange={(e) => setField("organization", e.target.value)}
                  className={`${inputBase} border-border`}
                  placeholder="Northside ISD"
                />
              </div>

              <div className="md:col-span-2">
                <label htmlFor="ssm-urgency" className="block text-silver text-[11px] uppercase tracking-[0.2em] mb-2">
                  Timeline
                </label>
                <select
                  id="ssm-urgency"
                  value={values.urgency}
                  onChange={(e) => setField("urgency", e.target.value)}
                  aria-invalid={!!errors.urgency}
                  className={`${inputBase} ${err("urgency")} [&>option]:bg-navy [&>option]:text-white`}
                >
                  <option value="">Select a timeline…</option>
                  {URGENCY.map((u) => (
                    <option key={u} value={u}>
                      {u}
                    </option>
                  ))}
                </select>
                {errors.urgency && (
                  <p className="text-red-400 text-xs mt-1.5">{errors.urgency}</p>
                )}
              </div>

              <div className="md:col-span-2">
                <label htmlFor="ssm-situation" className="block text-silver text-[11px] uppercase tracking-[0.2em] mb-2">
                  What are you facing?
                </label>
                <textarea
                  id="ssm-situation"
                  rows={5}
                  maxLength={2000}
                  value={values.situation}
                  onChange={(e) => setField("situation", e.target.value)}
                  aria-invalid={!!errors.situation}
                  className={`${inputBase} ${err("situation")} resize-y`}
                  placeholder="Briefly describe the student, the setting, and the outcome you need."
                />
                {errors.situation && (
                  <p className="text-red-400 text-xs mt-1.5">{errors.situation}</p>
                )}
              </div>

              <div className="md:col-span-2 border-t border-border pt-6">
                <BookingSlotPicker
                  value={{ date: values.scheduledDate, time: values.scheduledTime }}
                  onChange={(slot) =>
                    setValues((prev) => ({
                      ...prev,
                      scheduledDate: slot.date,
                      scheduledTime: slot.time,
                    }))
                  }
                  error={errors.scheduledDate || errors.scheduledTime}
                />
                <p className="text-silver/70 text-[11px] mt-3 leading-relaxed">
                  Sessions run 45 minutes by video. We confirm your time by email —
                  if it&rsquo;s taken, we&rsquo;ll offer the nearest opening.
                </p>
              </div>
            </div>


            <div className="mt-7 flex flex-col sm:flex-row sm:items-center gap-3">
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center justify-center gap-2 bg-cta text-white font-display uppercase tracking-[0.2em] text-xs px-6 py-4 shadow-cta hover:brightness-110 transition-all disabled:opacity-60"
              >
                {submitting ? (
                  <>
                    Sending
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  </>
                ) : (
                  <>
                    Submit application
                    <ArrowRight className="h-3.5 w-3.5" />
                  </>
                )}
              </button>
              <p className="text-silver/70 text-[11px] leading-relaxed">
                Confidential. Used only to review your application — no spam, ever.
              </p>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
